import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconClipboardList,
    IconLink,
    IconLoader2,
    IconPrinter,
    IconRefresh,
    IconSearch,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    TRACKING_TYPE,
    fetchCaptcha,
    fetchTracking,
    normalizeTracking,
    toAsciiDigits,
} from './bhataTrackingApi';
import {
    buildBhataTrackingPrintHtml,
    getBhataTrackingLogoUrl,
} from './bhataTrackingTemplate';
import {
    buildBhataTrackingShareUrl,
    canShareBhataTracking,
    parseBhataTrackingShareParams,
} from './bhataTrackingShare';

const TOOL_PATH = '/tools/general/bhata-tracking';
const TOOL_SLUG = 'general/bhata-tracking';

const fieldClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

/** The department's API takes YYYY-MM-DD, which is also what the share link carries. */
const toIsoDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

const isoToDate = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
    if (!match) return null;
    const date = new Date(+match[1], +match[2] - 1, +match[3]);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Tailwind classes per timeline state — the on-screen twin of the print sheet's
 * palette. Only the two states the department itself names get a chip.
 */
const STATE_CLASSES = {
    done: {
        dot: 'bg-[#1E9E5E]',
        chip: 'border-[#BFE6D1] bg-[#E3F6EC] text-[#136C40] dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300',
    },
    running: {
        dot: 'bg-orange-500',
        chip: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-300',
    },
    unknown: {
        dot: 'bg-[#A8A099]',
        chip: 'border-[#E8E8EE] bg-[#F7F7F9] text-[#6B635C] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
    },
};

function FieldLabel({ htmlFor, children }) {
    return (
        <label htmlFor={htmlFor} className="mb-1.5 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-300">
            {children}<span className="text-red-500"> *</span>
        </label>
    );
}

function SummaryItem({ label, value, highlight = false }) {
    if (!value) return null;
    return (
        <div className={`rounded-xl border px-3.5 py-3 ${
            highlight
                ? 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-950/20'
                : 'border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900'
        }`}>
            <dt className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#8C847D] dark:text-zinc-500">
                {label}
            </dt>
            <dd className={`mt-1 break-words text-[14px] font-bold ${
                highlight ? 'text-orange-800 dark:text-orange-300' : 'text-[#1C1815] dark:text-zinc-100'
            }`}>
                {value}
            </dd>
        </div>
    );
}

export default function BhataTrackingGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const location = useLocation();
    const mountedRef = useRef(true);

    const [mode, setMode] = useState(TRACKING_TYPE.byTrackingNo);
    const [form, setForm] = useState({ trackingNo: '', nid: '', dateOfBirth: '', captcha: '' });
    const [captcha, setCaptcha] = useState({ token: '', image: '' });
    const [captchaLoading, setCaptchaLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [tracking, setTracking] = useState(null);
    const [error, setError] = useState('');

    // Editing a field does NOT drop the result on screen. The lookup that
    // produced it is still what the print sheet and the PDF describe, and
    // blanking the panel on every keystroke leaves the user staring at nothing
    // while they retype an id. It is replaced when the next lookup returns.
    const update = (key, value) => {
        setForm((previous) => ({ ...previous, [key]: value }));
        if (error) setError('');
    };

    const loadCaptcha = useCallback(async ({ keepError = false } = {}) => {
        setCaptchaLoading(true);
        if (!keepError) setError('');
        try {
            const next = await fetchCaptcha(lang);
            if (!mountedRef.current) return;
            setCaptcha(next);
            setForm((previous) => ({ ...previous, captcha: '' }));
        } catch (requestError) {
            if (!mountedRef.current) return;
            setCaptcha({ token: '', image: '' });
            setError(t(requestError?.code === 'NETWORK'
                ? 'bhataTracking.errors.network'
                : 'bhataTracking.errors.captchaLoad'));
        } finally {
            if (mountedRef.current) setCaptchaLoading(false);
        }
    }, [lang, t]);

    useEffect(() => {
        mountedRef.current = true;
        void loadCaptcha();
        return () => {
            mountedRef.current = false;
        };
    }, [loadCaptcha]);

    // A shared link arrives with the lookup already filled in — everything but
    // the captcha, which is deliberately not shareable.
    useEffect(() => {
        const shared = parseBhataTrackingShareParams(location.search);
        if (!shared) return;
        setMode(shared.mode);
        setForm((previous) => ({
            ...previous,
            trackingNo: shared.trackingNo,
            nid: shared.nid,
            dateOfBirth: shared.dateOfBirth,
            captcha: '',
        }));
        setTracking(null);
        setError('');
    }, [location.search]);

    const byTrackingNo = mode === TRACKING_TYPE.byTrackingNo;

    const hasInput = Boolean(form.trackingNo || form.nid || form.dateOfBirth || form.captcha);
    const canSubmit = Boolean(
        (byTrackingNo ? form.trackingNo : form.nid && form.dateOfBirth)
        && form.captcha
        && captcha.token
        && !captchaLoading,
    );

    const status = deriveToolStatus({
        hasInput,
        canRun: canSubmit,
        processing: loading,
        hasResult: Boolean(tracking),
        hasError: Boolean(error),
    });
    const statusText = getToolStatusText(status, t, {
        ready: t('bhataTracking.statusReady'),
        processing: t('bhataTracking.statusProcessing'),
        success: t('bhataTracking.statusSuccess'),
        error: t('bhataTracking.statusError'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((step) => t(`bhataTracking.howTo.step${step}`)),
        [t],
    );

    const switchMode = (nextMode) => {
        if (nextMode === mode) return;
        setMode(nextMode);
        setForm({ trackingNo: '', nid: '', dateOfBirth: '', captcha: '' });
        setTracking(null);
        setError('');
        void loadCaptcha();
    };

    const handleSubmit = async () => {
        if (!canSubmit || loading) return;
        setLoading(true);
        setError('');
        setTracking(null);
        recordToolUsage(TOOL_SLUG, { category: 'general' });

        try {
            const details = await fetchTracking({
                trackingType: mode,
                trackingNo: form.trackingNo,
                nid: form.nid,
                dateOfBirth: form.dateOfBirth,
                captchaToken: captcha.token,
                captchaValue: form.captcha,
                lang,
            });
            const normalized = normalizeTracking(details, lang);
            if (!mountedRef.current) return;
            setTracking(normalized);
            notifications.show({
                title: t('common.success'),
                message: t('bhataTracking.resultFound'),
                color: 'green',
            });
        } catch (requestError) {
            if (!mountedRef.current) return;
            const knownKey = {
                CAPTCHA_FAILED: 'bhataTracking.errors.captchaFailed',
                NOT_FOUND: 'bhataTracking.errors.notFound',
                NETWORK: 'bhataTracking.errors.network',
            }[requestError?.code];
            // Our own wording wins for the cases we recognize — the API answers
            // "Invalid CAPTCHA" in English even on a Bangla request, and it says
            // nothing at all about an id it cannot find. Anything else is passed
            // through as sent, since it is more specific than a generic line.
            setError(knownKey
                ? t(knownKey)
                : requestError?.message?.trim() || t('bhataTracking.errors.fetch'));
        } finally {
            if (mountedRef.current) setLoading(false);
            // A token is spent by the attempt either way, so the picture must change.
            void loadCaptcha({ keepError: true });
        }
    };

    const handleReset = () => {
        setForm({ trackingNo: '', nid: '', dateOfBirth: '', captcha: '' });
        setTracking(null);
        setError('');
        window.history.replaceState(null, '', TOOL_PATH);
        void loadCaptcha();
    };

    const summaryItems = useMemo(() => (tracking ? [
        [t('bhataTracking.fields.trackingNo'), tracking.applicationId],
        [t('bhataTracking.fields.nbr'), tracking.verificationNumber],
        [t('bhataTracking.fields.program'), tracking.programName],
        [t('bhataTracking.fields.office'), tracking.officeName],
        [t('bhataTracking.fields.applicationDate'), tracking.applicationDate],
        [t('bhataTracking.fields.applicationTime'), tracking.applicationTime],
    ].filter(([, value]) => value) : []), [tracking, t]);

    const exportLabels = useMemo(() => ({
        title: t('bhataTracking.title'),
        documentTagline: t('bhataTracking.documentTagline'),
        documentDisclaimer: t('bhataTracking.documentDisclaimer'),
        officialSource: t('bhataTracking.officialSource'),
        currentStatus: t('bhataTracking.currentStatus'),
        applicantDetails: t('bhataTracking.applicantDetails'),
        progress: t('bhataTracking.progress'),
        trackingNo: t('bhataTracking.fields.trackingNo'),
        states: {
            done: t('bhataTracking.states.done'),
            running: t('bhataTracking.states.running'),
        },
    }), [t]);

    const exportLogoUrl = useMemo(
        () => getBhataTrackingLogoUrl(window.location.origin, lang),
        [lang],
    );

    const handlePrint = useCallback(() => {
        if (!tracking) return;

        recordToolUsage(TOOL_SLUG, { category: 'general' });

        const html = buildBhataTrackingPrintHtml({
            tracking,
            summaryItems,
            labels: exportLabels,
            logoUrl: exportLogoUrl,
        });

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDoc = frameWindow?.document;
        if (!frameDoc || !frameWindow) {
            iframe.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        const cleanup = () => iframe.remove();
        frameWindow.addEventListener('afterprint', cleanup, { once: true });

        let printed = false;
        const triggerPrint = () => {
            if (printed) return;
            printed = true;
            frameWindow.focus();
            frameWindow.print();
            setTimeout(cleanup, 60000);
        };

        const waitForAssets = async () => {
            try {
                await frameDoc.fonts?.ready;
            } catch {
                // ignore font load errors
            }
            const images = Array.from(frameDoc.images || []);
            await Promise.all(images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            }));
        };

        void waitForAssets().then(triggerPrint);
        setTimeout(triggerPrint, 1500);
    }, [tracking, summaryItems, exportLabels, exportLogoUrl]);

    const shareForm = { mode, ...form };
    const canShare = canShareBhataTracking(shareForm);

    /**
     * Copy a link that reopens this tool with the same lookup already typed in.
     * The captcha is left out on purpose — its token is single-use, so a link
     * carrying one would be dead on arrival anyway.
     */
    const handleCopyLink = () => {
        if (!canShare) {
            notifications.show({
                title: t('common.error'),
                message: t('bhataTracking.shareRequiresInput'),
                color: 'yellow',
            });
            return;
        }

        const url = buildBhataTrackingShareUrl(window.location.origin, TOOL_PATH, shareForm);
        const notifyCopied = () => notifications.show({
            title: t('common.success'),
            message: t('bhataTracking.linkCopied'),
            color: 'green',
        });

        if (copyTextToClipboard(url)) {
            notifyCopied();
            return;
        }
        void navigator.clipboard?.writeText(url)
            .then(notifyCopied)
            .catch(() => notifications.show({ title: t('common.error'), message: t('bhataTracking.copyFailed'), color: 'red' }));
    };

    return (
        <ToolPageLayout
            slug={TOOL_SLUG}
            subtitleKey="bhataTracking.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('bhataTracking.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasInput || tracking ? handleReset : undefined}
            resetLabel={t('bhataTracking.reset')}
            showTrust={false}
            primaryAction={{
                label: loading ? t('bhataTracking.tracking') : t('bhataTracking.track'),
                onClick: handleSubmit,
                disabled: !canSubmit || loading,
                loading,
                icon: IconSearch,
            }}
            // Once a result is on screen, printing it and copying its link join
            // Track and Reset in the sticky bar, so every action on the page is
            // in one place at the foot of the screen.
            secondaryAction={tracking ? [
                {
                    key: 'print',
                    label: t('bhataTracking.print'),
                    onClick: handlePrint,
                    icon: IconPrinter,
                },
                {
                    key: 'copy-link',
                    label: t('bhataTracking.copyLink'),
                    onClick: handleCopyLink,
                    disabled: !canShare,
                    icon: IconLink,
                },
            ] : undefined}
            controls={(
                <div className="space-y-4">
                    <p className="text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('bhataTracking.settingsHint')}
                    </p>

                    <div className="inline-flex w-full gap-0.5 rounded-lg border border-[#ECE8E4] bg-[#F7F7F9] p-0.5 dark:border-zinc-700 dark:bg-zinc-800">
                        {[
                            { id: TRACKING_TYPE.byTrackingNo, label: t('bhataTracking.modes.trackingNo') },
                            { id: TRACKING_TYPE.byNid, label: t('bhataTracking.modes.nid') },
                        ].map(({ id, label }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => switchMode(id)}
                                className={`flex-1 rounded-md px-3 py-2 text-[12px] font-semibold transition-colors ${
                                    mode === id
                                        ? 'bg-orange-500 text-white'
                                        : 'text-[#6B635C] hover:text-[#3D3833] dark:text-zinc-400 dark:hover:text-zinc-200'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {byTrackingNo ? (
                        <div>
                            <FieldLabel htmlFor="bhata-tracking-no">{t('bhataTracking.fields.trackingNo')}</FieldLabel>
                            <input
                                id="bhata-tracking-no"
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                value={form.trackingNo}
                                onChange={(event) => update('trackingNo', toAsciiDigits(event.target.value).slice(0, 20))}
                                placeholder={t('bhataTracking.placeholders.trackingNo')}
                                className={fieldClass}
                            />
                        </div>
                    ) : (
                        <>
                            <div>
                                <FieldLabel htmlFor="bhata-nid">{t('bhataTracking.fields.nbr')}</FieldLabel>
                                <input
                                    id="bhata-nid"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={form.nid}
                                    onChange={(event) => update('nid', toAsciiDigits(event.target.value).slice(0, 20))}
                                    placeholder={t('bhataTracking.placeholders.nid')}
                                    className={fieldClass}
                                />
                            </div>
                            <div>
                                <FieldLabel htmlFor="bhata-dob">{t('bhataTracking.fields.dateOfBirth')}</FieldLabel>
                                <DatePickerField
                                    id="bhata-dob"
                                    value={isoToDate(form.dateOfBirth)}
                                    onChange={(date) => update('dateOfBirth', date ? toIsoDate(date) : '')}
                                    placeholder={t('bhataTracking.placeholders.dateOfBirth')}
                                    triggerClassName={fieldClass}
                                    fromYear={1900}
                                    toYear={new Date().getFullYear()}
                                    disabled={{ after: new Date() }}
                                    clearable
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <FieldLabel htmlFor="bhata-captcha">{t('bhataTracking.fields.captcha')}</FieldLabel>
                        <div className="mb-2 flex items-center gap-2">
                            <div className="flex h-[46px] flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900">
                                {captchaLoading ? (
                                    <span className="text-[11px] font-semibold text-[#8C847D] dark:text-zinc-500">
                                        {t('bhataTracking.captchaLoading')}
                                    </span>
                                ) : captcha.image ? (
                                    <img
                                        src={captcha.image}
                                        alt={t('bhataTracking.captchaAlt')}
                                        className="h-full w-auto object-contain"
                                    />
                                ) : (
                                    <span className="text-[11px] font-semibold text-red-600 dark:text-red-400">
                                        {t('bhataTracking.captchaUnavailable')}
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => loadCaptcha()}
                                disabled={captchaLoading || loading}
                                title={t('bhataTracking.captchaRefresh')}
                                className="flex h-[46px] w-[46px] items-center justify-center rounded-xl border border-[#ECE8E4] bg-[#F7F7F9] text-[#6B635C] transition-colors hover:border-orange-300 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400"
                            >
                                <IconRefresh size={16} className={captchaLoading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                        <input
                            id="bhata-captcha"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={form.captcha}
                            onChange={(event) => update('captcha', event.target.value.slice(0, 10))}
                            placeholder={t('bhataTracking.placeholders.captcha')}
                            className={fieldClass}
                        />
                    </div>

                    {error ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-[12.5px] font-semibold leading-relaxed text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300" role="alert">
                            {error}
                        </p>
                    ) : null}

                    {/* The action bar's Track button sits at the bottom of the page,
                        which on a tall form means scrolling away from the captcha
                        you just read. This one stays with the fields. */}
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit || loading}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-orange-500 to-orange-600 px-4 py-3 text-[14px] font-semibold text-white shadow-md transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {loading ? <IconLoader2 size={16} className="animate-spin" /> : <IconSearch size={16} />}
                        {loading ? t('bhataTracking.tracking') : t('bhataTracking.track')}
                    </button>

                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('bhataTracking.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('bhataTracking.enterDetails')}
                    idleHint={t('bhataTracking.previewEmptyHint')}
                    showIdle={!tracking}
                    tall
                    fill
                    flushContent
                >
                    {!tracking ? (
                        // The panel's own idle art only shows while the status is
                        // `empty`; the moment a field is typed the status turns
                        // `ready` and this stands in, so the panel is never blank.
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 text-purple-500 dark:bg-purple-900/20 dark:text-purple-400">
                                <IconClipboardList size={27} stroke={1.6} />
                            </div>
                            <div>
                                <div className="text-[15px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('bhataTracking.enterDetails')}
                                </div>
                                <div className="mt-0.5 text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('bhataTracking.previewEmptyHint')}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-lg bg-[#FBFAF9] p-3 dark:bg-zinc-950 sm:p-4">
                            <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-6 text-center dark:border-orange-900/40 dark:bg-orange-950/20">
                                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B635C] dark:text-zinc-400">
                                    {t('bhataTracking.currentStatus')}
                                </p>
                                <p className="mt-3 text-[20px] font-black leading-tight text-orange-800 dark:text-orange-300 sm:text-[24px]">
                                    {tracking.latestStep?.title}
                                </p>
                                {tracking.applicationId ? (
                                    <span className="mt-4 inline-flex rounded-full border border-orange-200 bg-white px-4 py-1.5 text-[12px] font-bold text-orange-800 dark:border-orange-900/40 dark:bg-zinc-900 dark:text-orange-300">
                                        {t('bhataTracking.fields.trackingNo')}: {tracking.applicationId}
                                    </span>
                                ) : null}
                            </div>

                            <div className="rounded-2xl border border-[#ECE8E4] bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                                <p className="mb-3 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('bhataTracking.applicantDetails')}
                                </p>
                                <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {summaryItems.map(([label, value], index) => (
                                        <SummaryItem key={label} label={label} value={value} highlight={index === 0} />
                                    ))}
                                </dl>
                            </div>

                            <div className="rounded-2xl border border-[#ECE8E4] bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                                <p className="mb-4 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('bhataTracking.progress')}
                                </p>
                                <ol className="space-y-0">
                                    {tracking.steps.map((step, index) => {
                                        const styles = STATE_CLASSES[step.stepState] || STATE_CLASSES.unknown;
                                        const isLast = index === tracking.steps.length - 1;
                                        return (
                                            <li key={`${step.key}-${index}`} className="flex gap-3">
                                                <div className="flex w-3 flex-none flex-col items-center">
                                                    <span className={`mt-1.5 h-3 w-3 flex-none rounded-full ${styles.dot}`} />
                                                    {isLast ? null : (
                                                        <span className="w-0.5 flex-1 bg-[#ECE8E4] dark:bg-zinc-700" />
                                                    )}
                                                </div>
                                                <div className={`flex-1 ${isLast ? '' : 'pb-4'}`}>
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <p className="text-[13px] font-bold text-[#1C1815] dark:text-zinc-100">
                                                            {step.title}
                                                        </p>
                                                        {step.stepState === 'unknown' ? null : (
                                                            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${styles.chip}`}>
                                                                {t(`bhataTracking.states.${step.stepState}`)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {step.date ? (
                                                        <p className="mt-1 text-[11px] font-semibold text-[#8C847D] dark:text-zinc-500">
                                                            {[step.date, step.time].filter(Boolean).join(' · ')}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>

                            <p className="text-center text-[11px] text-[#9ca3af] dark:text-zinc-600">
                                {t('bhataTracking.officialSource')}
                            </p>
                        </div>
                    )}
                </ToolPreviewPanel>
            )}
        />
    );
}
